// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// API
import { deleteTimelineIntervention } from "../../api";

export const useDeleteIntervention = (
  clinicId: string | undefined,
  patientId: string
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (interventionId: string) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return deleteTimelineIntervention(
          token,
          clinicId,
          patientId,
          interventionId
        );
      } catch (error) {
        throw error;
      }
    },
    // Invalidate query cache
    onSuccess: (_) => {
      queryClient.invalidateQueries({
        queryKey: ["timeline-interventions", clinicId, patientId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Intervention deleted successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Intervention could not be deleted!"),
      });
    },
  });
};
